import { ELocalStatus } from "../../../../models/enums/localStatus";
import { ETool } from "../tools/Etool";
import * as d3 from "d3";
import ToolProvider from "../tools/toolProvider";

export interface ICircleCreationEvent {
    openPopup: () => void;
}

export type CursorType = "default" | "pointer" | "crosshair";

export class Circle {
    private x: number = 0;
    private y: number = 0;
    private r: number = 0;
    private container: d3.Selection<SVGElement, any, HTMLElement, any>;
    private color: string;
    private selection:
        | d3.Selection<SVGCircleElement, any, HTMLElement, any>
        | undefined;
    public static colors = {
        green: "#94ca85",
        red: "#f44336",
        orange: "#fbc02d",
        grey: "#a6a39f",
        blue: "#1a77d2",
        magenta: "#a232a8",
        pink: "#d15492",
        purple: "#7b1fa2",
    };
    public static cursors = new Map([
        [ETool.View, "view"],
        [ETool.Pen, "edit"],
        [ETool.Eraser, "erase"],
        [ETool.Save, "save"],
    ]);
    statusColors: Map<ELocalStatus, string> = new Map([
        [ELocalStatus.Available, Circle.colors.green],
        [ELocalStatus.Booked, Circle.colors.red],
        [ELocalStatus.Occupied, Circle.colors.red],
        [ELocalStatus.Unavailable, Circle.colors.red],
        [ELocalStatus.ToDisinfect, Circle.colors.orange],
        [ELocalStatus.OverdueForDisinfection, Circle.colors.orange],
        [ELocalStatus.Undefined, Circle.colors.grey],
    ]);

    constructor(
        container: d3.Selection<SVGElement, any, HTMLElement, any>,
        x: number,
        y: number,
        r: number,
        color: string
    ) {
        this.x = x;
        this.y = y;
        this.r = r;
        this.color = color;
        this.container = container;
    }

    private static getAttribeAsNumber(
        selection: d3.Selection<SVGCircleElement, any, HTMLElement, any>,
        name: string
    ) {
        return Number(selection.attr(name).replace("px", ""));
    }

    public static fromD3(
        container: d3.Selection<SVGElement, any, HTMLElement, any>,
        selection: d3.Selection<SVGCircleElement, any, HTMLElement, any>
    ) {
        const x = Circle.getAttribeAsNumber(selection, "cx");
        const y = Circle.getAttribeAsNumber(selection, "cy");
        const r = Circle.getAttribeAsNumber(selection, "r");
        const color = selection.attr("fill");

        const circle = new Circle(container, x, y, r, color);
        circle.selection = selection;

        return circle;
    }

    setId(id: number) {
        this.select?.attr("id", `cp${id}`).style("fill", Circle.colors.pink);
    }

    getId() {
        const id = this.select?.attr("id").replace("cp", "");
        return id ? Number(id) : undefined;
    }

    remove() {
        this.select?.remove();
    }

    on(
        eventName: string,
        listener: (event: React.MouseEvent<HTMLButtonElement>) => void
    ) {
        this.select?.on(eventName, listener);

        return this;
    }

    changeColor(isApplyAnimation: boolean, color: string, delay: number = 700) {
        const t = d3
            .transition()
            .duration(350)
            .ease(d3.easeCircle)
            .delay(delay);
        this.color = color;

        return isApplyAnimation
            ? this.select
                  ?.attr("r", this.r * 1.62)
                  .style("fill", d3.rgb(this.color).brighter(0.2).toString())
                  .transition(t)
                  .style("fill", this.color)
                  .attr("r", this.r)
            : this.select?.style("fill", this.color);
    }

    create() {
        this.selection = this.container
            .append("circle")
            .attr("cx", this.x)
            .attr("cy", this.y)
            .attr("r", this.r)
            .style("fill", this.color);

        return this;
    }

    applyAnimations() {
        this.select?.on(
            "mouseenter",
            (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                this.select?.attr("r", this.r * 1.62);
            }
        );

        this.select?.on(
            "mouseout",
            (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                const t = d3.transition().duration(700).ease(d3.easeCircle);
                this.select?.transition(t).attr("r", this.r);
            }
        );

        this.select?.on(
            "mouseover.cursor",
            (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                const tool = ToolProvider.GetInstance()?.toolType || ETool.View;
                this.select?.attr("class", Circle.cursors.get(tool) || "view");
            }
        );

        return this;
    }

    get select() {
        return this.selection;
    }

    get radius() {
        return this.r;
    }
}
